import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Color from 'color';

import Card from 'WeployDesignLanguage/Card';
import Typography from 'WeployDesignLanguage/Typography';
import { useMediaQuery, createBreakpoints } from 'WeployDesignLanguage/mediaQueries';

import Avatar from 'LegacyWeployDesignLanguage/Avatar';
import Grid from 'LegacyWeployDesignLanguage/Grid';
import { Tags } from 'LegacyWeployDesignLanguage/Tag';
import dagstorp from 'LegacyWeployDesignLanguage/dagstorp';

const MAX_TAGS = 2;

const BULLET_DIAMETER = '12px';
const BULLET_MARGIN = dagstorp.spacing[1];
const WORK_EXPERIENCES_ITEM_MARGIN = dagstorp.spacing[1];

const StyledListItem = styled.div`
  position: relative;
  margin-top: ${WORK_EXPERIENCES_ITEM_MARGIN};
  margin-bottom: ${WORK_EXPERIENCES_ITEM_MARGIN};
  margin-left: calc(${BULLET_DIAMETER} + (${BULLET_MARGIN}*2));

  &:before {
    background-color: ${props => props.theme.colors.white};

    ${props => props.isHovered && css`
      background-color: #e9efee; /* Need to hardcode this as a solid hex colour as fade shows the lines behind the circle */
    `}

    border-radius: 50%;
    border: 2px solid ${props => props.theme.colors.environment.divider};
    content: '';
    display: block;
    height: ${BULLET_DIAMETER};
    left: calc(-1*(${BULLET_DIAMETER} + (${BULLET_MARGIN}*2)));
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: ${BULLET_DIAMETER};
    z-index: 2;
  }

  &:after {
    border-left: 2px dashed ${props => props.theme.colors.environment.divider};
    content: '';
    display: block;
    height: calc(100% + ${WORK_EXPERIENCES_ITEM_MARGIN});
    left: calc(-1*(${BULLET_DIAMETER} - (${BULLET_DIAMETER}/2) + (${BULLET_MARGIN}*2)));
    position: absolute;
    top: 50%;
    width: 1px;
    z-index: 1;
  }

  &:last-child {
    &:after {
      display: none;
      height: 0;
    }
  }
`;

const StyledCard = styled(Card)`
  padding: ${props => props.theme.spacing[2]};
  border-radius: ${props => props.theme.borderRadius.small};
  box-shadow: ${props => props.theme.boxShadow.paper};
  margin: 0;

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => Color(theme.colors.text.primary).fade(0.90).string()};
  }
`;

const StyledCardDetails = styled.div`
  display: flex;
  flex-direction: ${props => (props.isMobileDevice ? ' column' : 'row')};
  justify-content: space-between;
  text-align: center;
`;

const StyledMobileWrapper = styled.div`
  margin-right: ${props => props.theme.spacing[2]};

  ${props => props.isMobileDevice && css`
    margin: auto;
    margin-bottom: ${props.theme.spacing[2]};
    text-align: center;
  `};
`;

const StyledAvailabilityDetails = styled.div`
  padding-left: ${props => props.theme.spacing[3]};
`;

const StyledTagWrapper = styled.div`
  margin-top: ${props => props.theme.spacing[1]};
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const StyledInitials = styled.div`
  color: ${props => props.theme.colors.text.primaryOnDark};
  font-size: 18px;
  letter-spacing: 2px;
  line-height: 0;
`;

const truncatedList = (listItems, maxLength, truncationText) => {
  if (listItems.length <= maxLength) {
    return listItems;
  }
  const numRemainingListItems = listItems.length - maxLength;
  return [...listItems.slice(0, maxLength), `+${numRemainingListItems}${truncationText ? ` ${truncationText}` : ''}`];
};

const WeployeeProfileSummary = ({
  availabilities,
  availabilitySchedule,
  avatarColour,
  id,
  initials,
  onClick,
  skills,
  state,
  systemsKnowledges,
  workExperiences,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { DOWN_SM } = createBreakpoints();
  const isMobileDevice = useMediaQuery(DOWN_SM);

  return (
    <StyledCard
      key={id}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      <StyledCardDetails
        isMobileDevice={isMobileDevice}
        onClick={() => onClick(id, avatarColour)}
      >
        <StyledMobileWrapper isMobileDevice={isMobileDevice}>
          <Avatar
            width="40px"
            height="40px"
            text={
              <StyledInitials>
                {initials}
              </StyledInitials>
            }
            backgroundColor={avatarColour}
          />
        </StyledMobileWrapper>
        <Grid container item direction={isMobileDevice ? 'column' : 'row'} align="left">
          <Grid item xs={12} md={6}>
            <StyledMobileWrapper isMobileDevice={isMobileDevice}>
              <Typography variant="h6">
                {`${initials} • Weployee Member`}
              </Typography>
              { isMobileDevice &&
                <StyledAvailabilityDetails>
                  <Typography variant="caption">
                    {`${state} • ${availabilities} • ${availabilitySchedule}`}
                  </Typography>
                </StyledAvailabilityDetails>
              }
            </StyledMobileWrapper>
            { isMobileDevice && <Typography variant="subtitle1">Work Experience</Typography> }
            <div>
              {
                truncatedList(workExperiences, MAX_TAGS, 'more').map(workExperience => (
                  <StyledListItem key={workExperience} isHovered={isHovered}>
                    <Typography variant="body1">{workExperience}</Typography>
                  </StyledListItem>
                ))
              }
            </div>
            { !isMobileDevice &&
              <StyledAvailabilityDetails>
                <Typography variant="caption">
                  {`${state} • ${availabilities} • ${availabilitySchedule}`}
                </Typography>
              </StyledAvailabilityDetails>
            }
          </Grid>
          <Grid item align="left" xs={12} md={6}>
            <Typography variant="subtitle1">Skills</Typography>
            <StyledTagWrapper>
              <Tags tags={truncatedList(skills, MAX_TAGS)} />
            </StyledTagWrapper>
            <Typography variant="subtitle1">Software and Tools</Typography>
            <StyledTagWrapper>
              <Tags tags={truncatedList(systemsKnowledges, MAX_TAGS)} />
            </StyledTagWrapper>
          </Grid>
        </Grid>
      </StyledCardDetails>
    </StyledCard>
  );
};


WeployeeProfileSummary.propTypes = {
  availabilities: PropTypes.string.isRequired,
  availabilitySchedule: PropTypes.string.isRequired,
  avatarColour: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  initials: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  skills: PropTypes.arrayOf(PropTypes.string).isRequired,
  state: PropTypes.string.isRequired,
  systemsKnowledges: PropTypes.arrayOf(PropTypes.string).isRequired,
  workExperiences: PropTypes.arrayOf(PropTypes.string).isRequired,
};

WeployeeProfileSummary.defaultProps = {
  onClick: () => {},
};

WeployeeProfileSummary.COLORS = Avatar.COLORS;

export default WeployeeProfileSummary;
